import React, { useState, useEffect, useMemo } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, updateDoc, getDocs, query, where, collection } from 'firebase/firestore';
import { db } from '../firebase';
import { useUser } from './UserContext';
import { Dialog, DialogTitle, DialogBody, DialogActions } from './dialog';
import { Button } from './button';
import { Input } from './input';
import { Field, Label } from './fieldset';
import { Text } from './text';
import { Heading } from './heading';
import {
  CheckIcon,
  ClockIcon,
  ClipboardDocumentIcon,
  UserPlusIcon,
  UserIcon,
} from '@heroicons/react/24/outline';

const InviteConvenioUserDialog = ({ isOpen, onClose, onCreated, convenioId, convenioName, proyectos = [] }) => {
  const { userData } = useUser();
  const [mode, setMode] = useState('new'); // 'new' or 'existing'
  const [nombre, setNombre] = useState('');
  const [correo, setCorreo] = useState('');
  const [accessType, setAccessType] = useState('full');
  const [notificar, setNotificar] = useState(true);
  const [selectedProyectoIds, setSelectedProyectoIds] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [copyTooltip, setCopyTooltip] = useState('');
  const [orgUsers, setOrgUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userSearchTerm, setUserSearchTerm] = useState('');

  // Fetch all org users when dialog opens in existing mode
  useEffect(() => {
    if (!isOpen || !userData?.org) return;

    const fetchOrgUsers = async () => {
      setUsersLoading(true);
      try {
        const usersQuery = query(
          collection(db, 'usuarios'),
          where('org', '==', userData.org)
        );
        const snapshot = await getDocs(usersQuery);
        const users = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setOrgUsers(users);
      } catch (err) {
        console.error('Error fetching org users:', err);
      } finally {
        setUsersLoading(false);
      }
    };

    fetchOrgUsers();
  }, [isOpen, userData?.org]);

  // Only show client users — owners/operators don't need convenio access
  const clientUsers = useMemo(() => {
    return orgUsers.filter((u) => u.rol === 'client');
  }, [orgUsers]);

  const filteredUsers = useMemo(() => {
    if (!userSearchTerm) return clientUsers;
    const term = userSearchTerm.toLowerCase();
    return clientUsers.filter((u) =>
      (u.nombre || '').toLowerCase().includes(term) ||
      (u.correo || '').toLowerCase().includes(term)
    );
  }, [clientUsers, userSearchTerm]);

  const handleClose = () => {
    if (isCreating) return;
    setMode('new');
    setNombre('');
    setCorreo('');
    setAccessType('full');
    setNotificar(true);
    setSelectedProyectoIds([]);
    setSelectedUserId(null);
    setUserSearchTerm('');
    setError('');
    setResult(null);
    onClose();
  };

  const toggleProyectoId = (proyectoId) => {
    setSelectedProyectoIds((prev) =>
      prev.includes(proyectoId)
        ? prev.filter((id) => id !== proyectoId)
        : [...prev, proyectoId]
    );
  };

  const buildConvenioAccess = () => ({
    [convenioId]: {
      fullAccess: accessType === 'full',
      proyectoIds: accessType === 'projects' ? selectedProyectoIds : [],
    },
  });

  const handleCreateNew = async () => {
    if (!nombre.trim() || !correo.trim()) {
      setError('Por favor completa nombre y correo.');
      return;
    }
    if (accessType === 'projects' && selectedProyectoIds.length === 0) {
      setError('Selecciona al menos un proyecto.');
      return;
    }

    setIsCreating(true);
    setError('');

    try {
      const functions = getFunctions();
      const createClientUser = httpsCallable(functions, 'createClientUser');
      const res = await createClientUser({
        nombre: nombre.trim(),
        correo: correo.trim(),
        clientIds: [],
        convenioAccess: buildConvenioAccess(),
        notificarPorCorreo: notificar,
      });
      setResult(res.data);
      if (onCreated) onCreated();
    } catch (err) {
      console.error('Error creating convenio user:', err);
      let msg = 'Error al crear el usuario.';
      if (err.code === 'functions/already-exists' || err.message === 'email-already-in-use') {
        msg = 'Este correo ya tiene cuenta. Usa "Usuario existente" para agregar acceso al convenio.';
      } else if (err.code === 'functions/invalid-argument' || err.message === 'invalid-email') {
        msg = 'Correo electrónico inválido.';
      } else if (err.code === 'functions/permission-denied') {
        msg = 'No tienes permisos para crear usuarios.';
      }
      setError(msg);
    } finally {
      setIsCreating(false);
    }
  };

  const handleAddExisting = async () => {
    if (!selectedUserId) {
      setError('Selecciona un usuario.');
      return;
    }
    if (accessType === 'projects' && selectedProyectoIds.length === 0) {
      setError('Selecciona al menos un proyecto.');
      return;
    }

    setIsCreating(true);
    setError('');

    try {
      const selectedUser = orgUsers.find((u) => u.id === selectedUserId);
      if (!selectedUser) {
        setError('Usuario no encontrado.');
        setIsCreating(false);
        return;
      }

      const existingAccess = selectedUser.convenioAccess || {};

      // Merge new access with existing
      const newAccess = {
        ...existingAccess,
        ...buildConvenioAccess(),
      };

      await updateDoc(doc(db, 'usuarios', selectedUserId), {
        convenioAccess: newAccess,
      });

      setResult({
        userName: selectedUser.nombre || selectedUser.correo,
        userEmail: selectedUser.correo,
        existingUser: true,
      });
      if (onCreated) onCreated();
    } catch (err) {
      console.error('Error adding convenio access:', err);
      setError('Error al agregar acceso. Intenta de nuevo.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleSubmit = () => {
    if (mode === 'new') {
      handleCreateNew();
    } else {
      handleAddExisting();
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyTooltip('Copiado!');
      setTimeout(() => setCopyTooltip(''), 2000);
    } catch {
      setCopyTooltip('Error');
      setTimeout(() => setCopyTooltip(''), 2000);
    }
  };

  const renderAccessOptions = () => (
    <>
      <div>
        <Text className="text-sm font-medium mb-2 block">Nivel de acceso *</Text>
        <div className="space-y-2">
          <label className={`flex items-center gap-3 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
            accessType === 'full' ? 'border-azul-surelia bg-azul-surelia/5' : 'border-gray-200 hover:bg-gray-50'
          }`}>
            <input
              type="radio"
              name="accessType"
              value="full"
              checked={accessType === 'full'}
              onChange={() => setAccessType('full')}
              disabled={isCreating}
              className="text-azul-surelia focus:ring-azul-surelia"
            />
            <div>
              <Text className="text-sm font-medium">Acceso completo al convenio</Text>
              <Text className="text-xs text-gray-500">
                Puede ver todos los contratos y proyectos
              </Text>
            </div>
          </label>

          {proyectos.length > 0 && (
            <label className={`flex items-center gap-3 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
              accessType === 'projects' ? 'border-azul-surelia bg-azul-surelia/5' : 'border-gray-200 hover:bg-gray-50'
            }`}>
              <input
                type="radio"
                name="accessType"
                value="projects"
                checked={accessType === 'projects'}
                onChange={() => setAccessType('projects')}
                disabled={isCreating}
                className="text-azul-surelia focus:ring-azul-surelia"
              />
              <div>
                <Text className="text-sm font-medium">Solo proyectos específicos</Text>
                <Text className="text-xs text-gray-500">
                  Limitar acceso a los proyectos seleccionados
                </Text>
              </div>
            </label>
          )}
        </div>
      </div>

      {accessType === 'projects' && proyectos.length > 0 && (
        <div>
          <Text className="text-sm font-medium mb-2 block">Proyectos *</Text>
          <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
            {proyectos.map((proyecto) => (
              <label
                key={proyecto.id}
                className={`flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                  selectedProyectoIds.includes(proyecto.id) ? 'bg-azul-surelia/5' : ''
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedProyectoIds.includes(proyecto.id)}
                  onChange={() => toggleProyectoId(proyecto.id)}
                  disabled={isCreating}
                  className="rounded border-gray-300 text-azul-surelia focus:ring-azul-surelia"
                />
                <Text className="text-sm font-medium">{proyecto.nombre}</Text>
              </label>
            ))}
          </div>
        </div>
      )}
    </>
  );

  return (
    <Dialog open={isOpen} onClose={handleClose} size="lg">
      <DialogTitle>Dar Acceso al Convenio</DialogTitle>
      <DialogBody>
        {result ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-green-700">
              <CheckIcon className="w-5 h-5" />
              <Heading level={3}>
                {result.existingUser ? 'Acceso agregado' : 'Usuario creado'}
              </Heading>
            </div>

            <div className="space-y-2">
              <div>
                <Text className="text-sm font-medium text-gray-500">Nombre</Text>
                <Text>{result.userName}</Text>
              </div>
              <div>
                <Text className="text-sm font-medium text-gray-500">Correo</Text>
                <Text>{result.userEmail}</Text>
              </div>
            </div>

            {result.emailSent && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <Text className="text-sm text-green-800">
                  Se envió un correo de bienvenida a {result.userEmail} con el
                  enlace para establecer su contraseña.
                </Text>
              </div>
            )}

            {result.existingUser && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <Text className="text-sm text-green-800">
                  El usuario ya puede ver este convenio con su cuenta existente.
                </Text>
              </div>
            )}

            {result.resetLink && (
              <div>
                <Text className="text-sm font-medium text-gray-500 mb-1">
                  Enlace para establecer contraseña:
                </Text>
                <div className="bg-gray-50 p-3 rounded-lg border break-all text-sm font-mono flex items-center justify-between gap-2">
                  <span className="flex-1">{result.resetLink}</span>
                  <div className="relative">
                    <button
                      onClick={() => copyToClipboard(result.resetLink)}
                      className="p-2 hover:bg-gray-200 rounded transition-colors flex-shrink-0"
                    >
                      <ClipboardDocumentIcon className="w-4 h-4 text-gray-600" />
                    </button>
                    {copyTooltip && (
                      <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                        {copyTooltip}
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-2 flex items-start gap-2 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <ClockIcon className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <Text className="text-sm text-yellow-800">
                    <strong>Importante:</strong> Este enlace expira en 1 hora.
                  </Text>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Mode selector */}
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => { setMode('new'); setError(''); }}
                className={`flex items-center gap-2 px-4 py-3 rounded-lg border text-sm font-medium transition-colors ${
                  mode === 'new'
                    ? 'border-azul-surelia bg-azul-surelia/5 text-azul-surelia'
                    : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                }`}
              >
                <UserPlusIcon className="w-4 h-4" />
                Usuario nuevo
              </button>
              <button
                type="button"
                onClick={() => { setMode('existing'); setError(''); }}
                className={`flex items-center gap-2 px-4 py-3 rounded-lg border text-sm font-medium transition-colors ${
                  mode === 'existing'
                    ? 'border-azul-surelia bg-azul-surelia/5 text-azul-surelia'
                    : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                }`}
              >
                <UserIcon className="w-4 h-4" />
                Usuario existente
              </button>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <Text className="text-sm font-medium mb-0.5">Convenio</Text>
              <Text className="text-sm text-gray-700">{convenioName}</Text>
            </div>

            {mode === 'new' ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field>
                    <Label>Nombre completo *</Label>
                    <Input
                      value={nombre}
                      onChange={(e) => setNombre(e.target.value)}
                      placeholder="Nombre del usuario"
                      disabled={isCreating}
                    />
                  </Field>
                  <Field>
                    <Label>Correo electrónico *</Label>
                    <Input
                      type="email"
                      value={correo}
                      onChange={(e) => setCorreo(e.target.value)}
                      placeholder="usuario@empresa.com"
                      disabled={isCreating}
                    />
                  </Field>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificar}
                    onChange={() => setNotificar(!notificar)}
                    disabled={isCreating}
                    className="rounded border-gray-300 text-azul-surelia focus:ring-azul-surelia"
                  />
                  <Text className="text-sm font-medium">
                    Enviar correo de bienvenida al nuevo usuario
                  </Text>
                </label>
              </>
            ) : (
              <div>
                <Text className="text-sm font-medium mb-2 block">Seleccionar usuario *</Text>
                <Input
                  placeholder="Buscar por nombre o correo..."
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                  className="mb-2"
                  disabled={isCreating}
                />
                <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                  {usersLoading ? (
                    <div className="flex justify-center py-4">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-azul-surelia" />
                    </div>
                  ) : filteredUsers.length === 0 ? (
                    <div className="p-3 text-center">
                      <Text className="text-sm text-gray-400">No se encontraron usuarios.</Text>
                    </div>
                  ) : (
                    filteredUsers.map((u) => {
                      const alreadyHasAccess = u.convenioAccess && convenioId in u.convenioAccess;
                      return (
                        <label
                          key={u.id}
                          className={`flex items-center gap-3 px-3 py-2 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors ${
                            selectedUserId === u.id
                              ? 'bg-azul-surelia/5 border-l-2 border-l-azul-surelia'
                              : 'hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="radio"
                            name="existingUser"
                            checked={selectedUserId === u.id}
                            onChange={() => {
                              setSelectedUserId(u.id);
                              setCorreo(u.correo || '');
                            }}
                            disabled={isCreating}
                            className="text-azul-surelia focus:ring-azul-surelia"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <Text className="text-sm font-medium truncate">
                                {u.nombre || 'Sin nombre'}
                              </Text>
                              {alreadyHasAccess && (
                                <span className="text-xs px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">
                                  ya tiene acceso
                                </span>
                              )}
                            </div>
                            <Text className="text-xs text-gray-400">{u.correo}</Text>
                          </div>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
            )}

            {renderAccessOptions()}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <Text className="text-sm text-blue-800">
                <strong>Nota:</strong> El usuario tendrá acceso de solo lectura a los
                contratos y fianzas{accessType === 'projects' ? ' de los proyectos seleccionados' : ' del convenio'}.
              </Text>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <Text className="text-sm text-red-700">{error}</Text>
              </div>
            )}
          </div>
        )}
      </DialogBody>
      <DialogActions>
        {result ? (
          <Button onClick={handleClose}>Cerrar</Button>
        ) : (
          <>
            <Button variant="outline" onClick={handleClose} disabled={isCreating}>
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                isCreating ||
                (mode === 'new' && (!correo.trim() || !nombre.trim())) ||
                (mode === 'existing' && !selectedUserId) ||
                (accessType === 'projects' && selectedProyectoIds.length === 0)
              }
            >
              {isCreating ? 'Procesando...' : mode === 'new' ? 'Crear e Invitar' : 'Dar Acceso'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default InviteConvenioUserDialog;
